import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Zap } from "lucide-react";
import { MaskLines } from "./Reveal";
import { track } from "../../lib/api";

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      {/* Ambient magenta orb */}
      <motion.div
        style={{ y: yBg, scale: scaleBg }}
        className="absolute -top-40 -right-32 w-[42rem] h-[42rem] noise-ring rounded-full pointer-events-none"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(233,0,255,0.08),transparent_55%)] pointer-events-none" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 mb-8"
        >
          <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs text-zinc-300">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--magenta)] animate-pulse" />
            Estudio de desarrollo web y software a medida
          </span>
        </motion.div>

        <h1 className="font-extrabold tracking-tight leading-[1.03] text-4xl sm:text-6xl lg:text-[4.4rem]">
          <MaskLines
            lines={["Sitios y apps que", "convierten visitas"]}
            delay={0.15}
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-gradient"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              en clientes reales.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
        >
          Landing pages, sitios web, tiendas en línea y B2B, y apps web a la medida. Ejecución veloz,
          criterio de negocio y IA como acelerador interno, no como promesa.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={onBook}
            className="group inline-flex items-center gap-2 rounded-full bg-[var(--magenta)] text-white font-semibold px-7 py-4 hover-lift hover:magenta-glow"
          >
            Agendar llamada
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <a
            href="#casos"
            data-testid="hero-cases-btn"
            onClick={() => track("view_cases_click", null, { location: "hero" })}
            className="inline-flex items-center gap-2 rounded-full glass text-white font-medium px-7 py-4 hover:border-white/25 transition-colors duration-300"
          >
            Ver proyectos
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm text-zinc-500"
        >
          <div className="flex items-center gap-2">
            <Zap size={15} className="text-[var(--magenta)]" />
            Entrega en días, no meses
          </div>
          <div className="hidden sm:block w-px h-4 bg-white/10" />
          <div>Proyectos pequeños desde <span className="text-white font-semibold">$499 USD</span></div>
          <div className="hidden sm:block w-px h-4 bg-white/10" />
          <div>México · Mercado hispano en EUA</div>
        </motion.div>
      </motion.div>
    </section>
  );
};
